import { useAuth } from '@clerk/react';
import type { ReactNode } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router';

import { useRedirectTarget } from './redirect-target';

/** Clerk is still loading the session - usually a few frames, so no spinner. */
function Loading() {
  return <div aria-busy="true" className="min-h-svh" />;
}

/** Wraps the app shell: signed-out visitors go to /sign-in and come back after. */
export function RequireAuth({ children }: { children?: ReactNode }) {
  const { isLoaded, isSignedIn } = useAuth();
  const location = useLocation();

  if (!isLoaded) return <Loading />;
  if (!isSignedIn) {
    const target = `${location.pathname}${location.search}`;
    const search = target === '/' ? '' : `?redirect_url=${encodeURIComponent(target)}`;
    return <Navigate to={`/sign-in${search}`} replace />;
  }
  return children ?? <Outlet />;
}

/** Wraps the auth screens: someone already signed in has nothing to do there. */
export function RedirectIfSignedIn({ children }: { children?: ReactNode }) {
  const { isLoaded, isSignedIn } = useAuth();
  const target = useRedirectTarget();

  if (!isLoaded) return <Loading />;
  if (isSignedIn) return <Navigate to={target} replace />;
  return children ?? <Outlet />;
}
